/**
 * SelectionTray v5 — The Unified Bottom Workspace
 * ─────────────────────────────────────────────────
 * Tabs: [Collections] [Select] [Journey]
 *
 * FIX: handleGrabToSelection (useCallback) moved above the early `return null`
 * to comply with React's Rules of Hooks — hooks must never be called after
 * a conditional return statement.
 *
 * v5.1: Added onExpandChange callback — notifies Dashboard when the tray
 *       expands or collapses so MapControls can hide the "New Project" button.
 */

import React, {
  useState, useRef, useEffect, useCallback, useMemo,
} from 'react';
import {
  X, Search, GitFork, FolderTree, Check, Plus,
  ChevronUp, ChevronDown, Trash2, Sparkles, Star,
  Bookmark, Globe, Lock, Edit3, Layers, ArrowRight,
  GitBranch, Play, Pause, ChevronLeft, ChevronRight,
  Zap, Eye, Clock, Pencil, CheckCheck, MapPin,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { getProjectZone } from '../../config/zones';
import { Button } from '../ui';

// ─────────────────────────────────────────────────────────────────────────────
// ZONE COLOR MAP
// ─────────────────────────────────────────────────────────────────────────────

const ZONE_COLORS = {
  build:     { color: '#f59e0b', label: 'Build' },
  beautiful: { color: '#ec4899', label: 'Beautiful' },
  sound:     { color: '#06b6d4', label: 'Sound' },
  taste:     { color: '#fb923c', label: 'Taste' },
  story:     { color: '#8b5cf6', label: 'Story' },
  heal:      { color: '#ef4444', label: 'Heal' },
  smart:     { color: '#6366f1', label: 'Smart' },
  sell:      { color: '#22c55e', label: 'Sell' },
  default:   { color: '#71717a', label: '?' },
};

const JOURNEY_AUTO_PLAY_MS        = 3000;
const JOURNEY_TRANSITION_PAUSE_MS = 5000;
const IDLE_COLLAPSE_MS            = 5000;

function zoneInfo(key) {
  return ZONE_COLORS[key] || ZONE_COLORS.default;
}

function safeZone(project) {
  if (!project) return null;
  try { return getProjectZone(project); } catch { return null; }
}

// ─────────────────────────────────────────────────────────────────────────────
// SHARED SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const ZoneDot = ({ zoneKey, size = 'sm' }) => {
  const { color } = zoneInfo(zoneKey);
  const cls = size === 'xs' ? 'w-1.5 h-1.5' : 'w-2 h-2';
  return <span className={`${cls} rounded-full flex-shrink-0`} style={{ backgroundColor: color }} />;
};

const ProjectThumb = ({ project, size = 32 }) => {
  const zone      = safeZone(project);
  const img       = project?.image_url || project?.imageUrl || project?.cover_image || project?.coverImage;
  const zoneColor = zone?.color || '#71717a';
  const px        = `${size}px`;
  return (
    <div
      className="rounded-lg overflow-hidden flex-shrink-0 border"
      style={{ width: px, height: px, borderColor: `${zoneColor}40` }}
    >
      {img
        ? <img src={img} alt="" className="w-full h-full object-cover" />
        : (
          <div
            className="w-full h-full flex items-center justify-center font-bold"
            style={{
              fontSize: size < 28 ? '9px' : '11px',
              backgroundColor: `${zoneColor}12`,
              color: zoneColor,
            }}
          >
            {project?.name?.[0]?.toUpperCase() || '?'}
          </div>
        )
      }
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ZONE DIVERSITY BADGE
// ─────────────────────────────────────────────────────────────────────────────

const ZoneDiversityBadge = ({ selectedProjects }) => {
  const zones = useMemo(() => {
    const seen = new Map();
    selectedProjects.forEach(p => {
      const zone = safeZone(p);
      const key  = zone?.id || 'default';
      if (!seen.has(key)) seen.set(key, zone?.shortLabel || zoneInfo(key).label);
    });
    return [...seen.entries()];
  }, [selectedProjects]);

  if (zones.length < 2) return null;
  const isWild = zones.length >= 3;

  return (
    <div className={cn(
      'flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border transition-all duration-500',
      isWild ? 'bg-purple-500/10 border-purple-500/30' : 'bg-amber-500/8 border-amber-500/25',
    )}>
      <div className="flex items-center gap-1 flex-wrap">
        {zones.map(([key, label], i) => {
          const { color } = zoneInfo(key);
          return (
            <React.Fragment key={key}>
              {i > 0 && <span className="text-zinc-600 text-[9px]">×</span>}
              <span
                className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                style={{ color, backgroundColor: `${color}15` }}
              >
                {label}
              </span>
            </React.Fragment>
          );
        })}
      </div>
      {isWild && <Zap className="w-3 h-3 text-purple-400 flex-shrink-0" />}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SELECTED PROJECT PILL — Select tab
// ─────────────────────────────────────────────────────────────────────────────

const SelectedProjectPill = ({ project, onRemove, isJustAdded }) => {
  const zone = safeZone(project);
  return (
    <div className={cn(
      'group relative flex items-center gap-2 pl-1 pr-1.5 py-1 rounded-xl border',
      'transition-all duration-300 flex-shrink-0',
      'hover:border-zinc-600 hover:bg-zinc-800/80',
      isJustAdded
        ? 'bg-emerald-500/10 border-emerald-500/40 animate-in zoom-in-95 duration-300'
        : 'bg-zinc-800/60 border-zinc-700/40',
    )}>
      <ProjectThumb project={project} size={32} />
      <span className="text-[12px] font-medium text-zinc-300 max-w-[100px] truncate leading-tight">
        {project.name}
      </span>
      {zone && <ZoneDot zoneKey={zone.id || 'default'} size="xs" />}
      <button
        onClick={(e) => { e.stopPropagation(); onRemove?.(project.id); }}
        className="p-0.5 rounded-md text-zinc-600 hover:text-red-400 hover:bg-red-500/10 transition-all opacity-0 group-hover:opacity-100"
      >
        <X className="w-3 h-3" />
      </button>
      {isJustAdded && (
        <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-500 border-2 border-zinc-900">
          <div className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-50" />
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// STARRED PROJECT PILL — Collections tab
// ─────────────────────────────────────────────────────────────────────────────

const StarredPill = ({ project, isHighlighted, onFlyTo, onUnstar, onAddToSelection }) => {
  const zone      = safeZone(project);
  const zoneColor = zone?.color || '#71717a';
  return (
    <div
      className={cn(
        'group relative flex items-center gap-2 pl-1.5 pr-2 py-1.5 rounded-xl border',
        'transition-all duration-200 flex-shrink-0 cursor-pointer',
        isHighlighted
          ? 'bg-amber-500/15 border-amber-500/50 shadow-sm shadow-amber-500/10'
          : 'bg-zinc-900/80 border-zinc-700/50 hover:border-zinc-600 hover:bg-zinc-800/80',
      )}
      onClick={() => onFlyTo?.(project)}
    >
      <ProjectThumb project={project} size={34} />
      <div className="flex flex-col min-w-0">
        <span className={cn(
          'text-[11px] font-semibold truncate max-w-[90px] leading-tight',
          isHighlighted ? 'text-amber-200' : 'text-zinc-200',
        )}>
          {project.name}
        </span>
        {zone && (
          <span className="text-[9px] leading-tight font-medium" style={{ color: zoneColor }}>
            {zone.shortLabel}
          </span>
        )}
      </div>
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-1">
        <button
          onClick={(e) => { e.stopPropagation(); onAddToSelection?.(project); }}
          className="p-1 rounded-lg hover:bg-purple-500/20 transition-colors"
          title="Add to selection"
        >
          <Plus className="w-3 h-3 text-purple-400" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onUnstar?.(project.id); }}
          className="p-1 rounded-lg hover:bg-red-500/20 transition-colors"
          title="Remove star"
        >
          <Star className="w-3 h-3 text-amber-400 fill-amber-400 hover:text-red-400 hover:fill-transparent" />
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// RECENT PROJECT PILL — Collections tab
// ─────────────────────────────────────────────────────────────────────────────

const RecentPill = ({ project, onFlyTo, onAddToSelection }) => {
  const zone = safeZone(project);
  const zoneColor = zone?.color || '#71717a';
  return (
    <div
      className="group flex items-center gap-2 pl-1.5 pr-2 py-1.5 rounded-xl border bg-zinc-900/60 border-zinc-800/50 hover:border-zinc-700 hover:bg-zinc-800/70 transition-all cursor-pointer flex-shrink-0"
      onClick={() => onFlyTo?.(project)}
    >
      <ProjectThumb project={project} size={26} />
      <div className="flex flex-col min-w-0">
        <span className="text-[11px] text-zinc-300 truncate max-w-[80px] leading-tight group-hover:text-white font-medium transition-colors">
          {project.name}
        </span>
        {zone && (
          <span className="text-[9px] leading-tight" style={{ color: zoneColor }}>{zone.shortLabel}</span>
        )}
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onAddToSelection?.(project); }}
        className="p-1 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-purple-500/20 transition-all"
        title="Add to selection"
      >
        <Plus className="w-2.5 h-2.5 text-purple-400" />
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// BRANCH VIEWER — Journey-style tree for a single curated branch
// Uses the same TreeCard + TreeForkDivider as JourneyTab for visual consistency.
//
// View mode:  horizontal scroll of TreeCards connected by TreeForkDividers.
//             Single click → fly to on map.
//             "Open" button on hover → opens ProjectDetailOverlay.
// Edit mode:  each card gets an × badge. Search bar adds projects.
//             Save / Cancel in the header.
// ─────────────────────────────────────────────────────────────────────────────

const BranchViewer = ({
  branch, isActive, isOwned,
  onActivate, onEdit, onAddToSelection,
  onFlyToProject, onOpenProject,
  onRemoveFromBranch, onRenameBranch,
  onUpdateBranchProjects,
  onSearchProjects, allProjects = [],
  onDeleteBranch,
}) => {
  const [isEditing,  setIsEditing]  = useState(false);
  const [renaming,   setRenaming]   = useState(false);
  const [nameValue,  setNameValue]  = useState(branch.name || '');
  const [localIds,   setLocalIds]   = useState(branch.curatedProjectIds || []);
  const [saving,     setSaving]     = useState(false);
  const [searchQ,    setSearchQ]    = useState('');
  const [searchRes,  setSearchRes]  = useState([]);
  const [searching,  setSearching]  = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const nameRef     = useRef(null);
  const searchRef   = useRef(null);
  const searchTimer = useRef(null);

  useEffect(() => { if (!isEditing) setLocalIds(branch.curatedProjectIds || []); },
    [branch.curatedProjectIds, isEditing]);
  useEffect(() => { setNameValue(branch.name || ''); }, [branch.name]);
  useEffect(() => { if (renaming) setTimeout(() => nameRef.current?.focus(), 60); }, [renaming]);
  useEffect(() => { if (showSearch) setTimeout(() => searchRef.current?.focus(), 80); }, [showSearch]);

  useEffect(() => {
    if (!showSearch || searchQ.length < 2) { setSearchRes([]); return; }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const found = await onSearchProjects?.(searchQ);
        setSearchRes((found || []).filter(p => !localIds.includes(p.id)).slice(0, 6));
      } catch { setSearchRes([]); }
      setSearching(false);
    }, 220);
    return () => clearTimeout(searchTimer.current);
  }, [searchQ, localIds, showSearch, onSearchProjects]);

  const resolvedItems = useMemo(() => {
    const ids = isEditing ? localIds : (branch.curatedProjectIds || []);
    if (branch.previewProjects?.length > 0 && !isEditing) return branch.previewProjects;
    const m = new Map(allProjects.map(p => [p.id, p]));
    return ids.map(id => m.get(id)).filter(Boolean);
  }, [branch.previewProjects, branch.curatedProjectIds, localIds, isEditing, allProjects]);

  const count = isEditing ? localIds.length
    : (branch.curatedCount || branch.curatedProjectIds?.length || 0);

  const zoneSet = useMemo(() => {
    const seen = new Map();
    resolvedItems.forEach(p => { const z = safeZone(p); if (z && !seen.has(z.id)) seen.set(z.id, z); });
    return [...seen.values()].slice(0, 6);
  }, [resolvedItems]);
  const dominantColor = zoneSet[0]?.color || '#6366f1';

  const commitRename = () => {
    const t = nameValue.trim();
    if (t && t !== branch.name) onRenameBranch?.(branch.id, t);
    setRenaming(false);
  };

  const addProject = p => {
    setLocalIds(prev => prev.includes(p.id) ? prev : [...prev, p.id]);
    setSearchQ(''); setSearchRes([]); setShowSearch(false);
  };

  const removeProject = id => setLocalIds(prev => prev.filter(x => x !== id));

  const saveEdits = async () => {
    setSaving(true);
    try {
      // One atomic PUT with the full ID list — persists adds AND removes
      await onUpdateBranchProjects?.(branch.id, localIds);
      // Activate immediately so highlights appear on the map — no second save
      if (!isActive) {
        onActivate?.({ ...branch, curatedProjectIds: localIds, curatedCount: localIds.length });
      }
    } finally { setSaving(false); setIsEditing(false); }
  };

  const cancelEdit = () => {
    setLocalIds(branch.curatedProjectIds || []);
    setIsEditing(false); setShowSearch(false);
  };

  // Zone-tinted left border on active branches — clear visual signal
  const leftAccent = isEditing ? '#a855f7'
    : isActive ? dominantColor
    : 'transparent';

  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-200"
      style={{
        background: isEditing
          ? 'linear-gradient(135deg, rgba(168,85,247,0.07), rgba(9,9,11,0.95))'
          : isActive
          ? `linear-gradient(135deg, ${dominantColor}0a, rgba(9,9,11,0.95))`
          : 'rgba(18,18,22,0.8)',
        border: isEditing
          ? '1px solid rgba(168,85,247,0.35)'
          : isActive
          ? `1px solid ${dominantColor}40`
          : '1px solid rgba(39,39,42,0.7)',
        borderLeft: `3px solid ${leftAccent}`,
      }}
    >
      {/* ── Header ── */}
      <div
        className="flex items-center gap-2.5 px-3 py-2.5 cursor-pointer hover:bg-zinc-800/30 transition-colors"
        onClick={() => onActivate?.(branch)}
      >

        {/* Zone color dots — quick visual identity */}
        <div className="flex -space-x-1 flex-shrink-0">
          {zoneSet.slice(0, 4).map((z, i) => (
            <div key={z.id}
              className="w-3.5 h-3.5 rounded-full border border-black/40 flex-shrink-0"
              style={{ backgroundColor: z.color, zIndex: 4 - i,
                       boxShadow: `0 0 4px ${z.color}60` }} />
          ))}
          {zoneSet.length === 0 && (
            <FolderTree className="w-3.5 h-3.5 text-zinc-600" />
          )}
        </div>

        {/* Name */}
        <div className="flex-1 min-w-0">
          {renaming ? (
            <input ref={nameRef} value={nameValue}
              onChange={e => setNameValue(e.target.value)}
              onBlur={commitRename}
              onKeyDown={e => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') { setRenaming(false); setNameValue(branch.name || ''); }
              }}
              className="w-full bg-zinc-800 border border-purple-500/40 rounded-lg px-2 py-0.5 text-[12px] text-white outline-none focus:border-purple-400/60"
            />
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-[12px] font-bold truncate leading-tight"
                style={{ color: isActive ? '#fde68a' : isEditing ? '#e9d5ff' : '#e4e4e7' }}>
                {branch.name || `Collection #${branch.id}`}
              </span>
              {isActive && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
                  style={{ background: `${dominantColor}20`, color: dominantColor }}>
                  active
                </span>
              )}
              {branch.isPublic
                ? <Globe className="w-2.5 h-2.5 text-emerald-500/70 flex-shrink-0" />
                : <Lock className="w-2.5 h-2.5 text-zinc-700 flex-shrink-0" />}
            </div>
          )}
          <span className="text-[9px] text-zinc-600">
            {count} project{count !== 1 ? 's' : ''}
            {zoneSet.length > 1 && ` · ${zoneSet.length} zones`}
          </span>
        </div>

        {/* Actions — stopPropagation so button clicks don't trigger header's onActivate */}
        {!isEditing ? (
          <div className="flex items-center gap-0.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
            <button onClick={() => onActivate?.(branch)}
              className="p-1.5 rounded-lg transition-colors"
              style={{ color: isActive ? dominantColor : '#52525b' }}
              title={isActive ? 'Active on map' : 'Show on map'}>
              <Eye className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => onAddToSelection?.(branch)}
              className="p-1.5 rounded-lg text-zinc-600 hover:text-purple-400 transition-colors"
              title="Load to multi-select">
              <Layers className="w-3.5 h-3.5" />
            </button>
            {isOwned && (
              <>
                <button onClick={() => { setRenaming(true); }}
                  className="p-1.5 rounded-lg text-zinc-600 hover:text-zinc-300 transition-colors"
                  title="Rename">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => setIsEditing(true)}
                  className="p-1.5 rounded-lg text-zinc-600 hover:text-purple-400 transition-colors"
                  title="Edit collection">
                  <Edit3 className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => onDeleteBranch?.(branch)}
                  className="p-1.5 rounded-lg text-zinc-600 hover:text-red-400 transition-colors"
                  title="Delete collection">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
            <button onClick={cancelEdit}
              className="px-2 py-1 rounded-lg text-[10px] text-zinc-500 hover:text-zinc-300 transition-colors">
              Cancel
            </button>
            <button onClick={saveEdits} disabled={saving}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold text-purple-200 transition-colors disabled:opacity-50"
              style={{ background: 'rgba(168,85,247,0.2)', border: '1px solid rgba(168,85,247,0.4)' }}>
              {saving
                ? <div className="w-3 h-3 border border-purple-300 border-t-transparent rounded-full animate-spin" />
                : <CheckCheck className="w-3 h-3" />}
              Save
            </button>
          </div>
        )}
      </div>

      {/* ── Edit mode: add project bar ── */}
      {isEditing && (
        <div className="px-3 pb-2 relative">
          {showSearch ? (
            <div className="relative">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl"
                style={{ background: 'rgba(39,39,42,0.8)', border: '1px solid rgba(168,85,247,0.4)' }}>
                <Search className="w-3 h-3 text-purple-400 flex-shrink-0" />
                <input ref={searchRef} value={searchQ} onChange={e => setSearchQ(e.target.value)}
                  placeholder="Search projects to add…"
                  className="flex-1 bg-transparent text-[11px] text-white placeholder:text-zinc-600 outline-none" />
                {searching && <div className="w-3 h-3 border border-zinc-600 border-t-purple-400 rounded-full animate-spin" />}
                <button onClick={() => { setShowSearch(false); setSearchQ(''); setSearchRes([]); }}>
                  <X className="w-3 h-3 text-zinc-500 hover:text-zinc-300" />
                </button>
              </div>
              {searchRes.length > 0 && (
                <div className="absolute left-0 right-0 bottom-full mb-1 rounded-xl overflow-hidden z-50"
                  style={{ background: 'rgba(13,13,16,0.99)', border: '1px solid rgba(63,63,70,0.8)',
                           boxShadow: '0 -8px 32px rgba(0,0,0,0.6)', animation: 'trayDropdownIn 0.12s ease-out' }}>
                  {searchRes.map(p => {
                    const z = safeZone(p);
                    const img = p?.image_url || p?.imageUrl || p?.cover_image;
                    return (
                      <button key={p.id} onClick={() => addProject(p)}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-zinc-800/60 transition-colors">
                        <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 border"
                          style={{ borderColor: `${z?.color || '#71717a'}30` }}>
                          {img
                            ? <img src={img} alt="" className="w-full h-full object-cover" />
                            : <div className="w-full h-full flex items-center justify-center text-xs font-bold"
                                style={{ backgroundColor: `${z?.color}15`, color: z?.color }}>
                                {p.name?.[0]}
                              </div>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-semibold text-zinc-200 truncate">{p.name}</p>
                          {z && <p className="text-[9px] font-medium" style={{ color: z.color }}>{z.shortLabel}</p>}
                        </div>
                        <Plus className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <button onClick={() => setShowSearch(true)}
              className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-xl text-[11px] font-semibold transition-all"
              style={{ background: 'rgba(168,85,247,0.08)', border: '1px dashed rgba(168,85,247,0.3)', color: '#c084fc' }}>
              <Plus className="w-3.5 h-3.5" />
              Add project
            </button>
          )}
        </div>
      )}

      {/* ── Journey-style tree ── */}
      {resolvedItems.length === 0 ? (
        <div className="px-4 py-4 text-center">
          <p className="text-[11px] text-zinc-600">
            {count > 0 ? "Projects not in current map view." : "This collection is empty."}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto pb-3 px-3"
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#27272a transparent' }}>
          <div className="flex items-center gap-0 min-w-max">
            {resolvedItems.map((project, idx) => {
              const z   = safeZone(project) || {};
              const { color } = zoneInfo(z.id || 'default');
              const isCrossZone = idx > 0
                && (safeZone(resolvedItems[idx - 1])?.id || 'default') !== (z.id || 'default');
              return (
                <React.Fragment key={project.id}>
                  {idx > 0 && (
                    <TreeForkDivider
                      horizontal
                      crossZone={isCrossZone}
                      fromZone={safeZone(resolvedItems[idx - 1])?.id}
                      toZone={z.id}
                    />
                  )}
                  {/* Wrapper with edit badge + hover group */}
                  <div className="relative flex-shrink-0 group/node">
                    {/* Edit mode × badge */}
                    {isEditing && isOwned && (
                      <button
                        onClick={e => { e.stopPropagation(); removeProject(project.id); }}
                        className="absolute -top-1.5 -right-1.5 z-10 rounded-full flex items-center justify-center transition-all"
                        style={{ background: 'rgba(239,68,68,0.9)', border: '1.5px solid rgba(239,68,68,0.5)',
                                 width: 18, height: 18 }}>
                        <X className="w-2.5 h-2.5 text-white" />
                      </button>
                    )}
                    {/* TreeCard — identical to JourneyTab, click = fly to map */}
                    <TreeCard
                      project={project}
                      onClick={() => onFlyToProject?.(project)}
                    />
                    {/* "Open" button — appears on hover, opens ProjectDetailOverlay */}
                    {!isEditing && (
                      <button
                        onClick={e => { e.stopPropagation(); onOpenProject?.(project); }}
                        className="absolute bottom-1.5 right-1.5 z-10 opacity-0 group-hover/node:opacity-100 transition-opacity p-1 rounded-lg"
                        style={{ background: 'rgba(139,92,246,0.3)', border: '1px solid rgba(139,92,246,0.5)' }}
                        title="Open project detail">
                        <ArrowRight className="w-2.5 h-2.5 text-purple-200" />
                      </button>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};



// ─────────────────────────────────────────────────────────────────────────────
// SEARCH-TO-ADD — Select tab
// ─────────────────────────────────────────────────────────────────────────────

const SearchToAdd = ({ onSearch, onAdd, selectedIds, isVisible }) => {
  const [query, setQuery]             = useState('');
  const [results, setResults]         = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef     = useRef(null);
  const containerRef = useRef(null);
  const timer        = useRef(null);

  useEffect(() => {
    if (!isVisible) { setQuery(''); setResults([]); setShowResults(false); }
    else setTimeout(() => inputRef.current?.focus(), 100);
  }, [isVisible]);

  useEffect(() => {
    if (query.length < 2) { setResults([]); setShowResults(false); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const found    = await onSearch?.(query);
        const filtered = (found || []).filter(p => !selectedIds.includes(p.id));
        setResults(filtered.slice(0, 6));
        setShowResults(true);
      } catch { setResults([]); }
      setIsSearching(false);
    }, 250);
    return () => clearTimeout(timer.current);
  }, [query, selectedIds, onSearch]);

  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setShowResults(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (!isVisible) return null;

  return (
    <div ref={containerRef} className="relative mb-2">
      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800/80 border border-zinc-700/60 rounded-xl focus-within:border-purple-500/50 focus-within:ring-1 focus-within:ring-purple-500/20 transition-all">
        <Search className="w-3 h-3 text-zinc-500 flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (results.length > 0) setShowResults(true); }}
          placeholder="Search to add any project..."
          className="flex-1 bg-transparent text-[12px] text-white placeholder:text-zinc-600 focus:outline-none"
        />
        {isSearching && <div className="w-3 h-3 border border-zinc-500 border-t-purple-400 rounded-full animate-spin" />}
        {query && !isSearching && (
          <button onClick={() => { setQuery(''); setResults([]); }} className="p-0.5">
            <X className="w-2.5 h-2.5 text-zinc-600" />
          </button>
        )}
      </div>

      {showResults && results.length > 0 && (
        <div
          className="absolute bottom-full left-0 right-0 mb-2 bg-zinc-900/98 backdrop-blur-xl border border-zinc-700 rounded-xl shadow-2xl shadow-black/50 overflow-hidden z-50"
          style={{ animation: 'trayDropdownIn 0.15s ease-out' }}
        >
          <div className="max-h-52 overflow-y-auto">
            {results.map(project => {
              const zone = safeZone(project);
              return (
                <button
                  key={project.id}
                  onClick={() => { onAdd?.(project); setQuery(''); setResults([]); setShowResults(false); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-zinc-800/80 transition-colors text-left group"
                >
                  <ProjectThumb project={project} size={28} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-medium text-zinc-200 truncate">{project.name}</p>
                    <p className="text-[10px] text-zinc-500 truncate">
                      {project.author?.username ? `@${project.author.username}` : ''}
                      {zone ? ` · ${zone.shortLabel}` : ''}
                    </p>
                  </div>
                  <Plus className="w-3.5 h-3.5 text-zinc-600 group-hover:text-emerald-400 transition-colors flex-shrink-0" />
                </button>
              );
            })}
          </div>
          <div className="px-3 py-1.5 border-t border-zinc-800 bg-zinc-900/80">
            <p className="text-[10px] text-zinc-600">Click to add to selection</p>
          </div>
        </div>
      )}

      {showResults && results.length === 0 && query.length >= 2 && !isSearching && (
        <div
          className="absolute bottom-full left-0 right-0 mb-2 bg-zinc-900/98 border border-zinc-700 rounded-xl shadow-xl px-3 py-3 z-50"
          style={{ animation: 'trayDropdownIn 0.15s ease-out' }}
        >
          <p className="text-[11px] text-zinc-500 text-center">No matching projects</p>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// COLLECTIONS TAB CONTENT — redesigned
// ─────────────────────────────────────────────────────────────────────────────

const CollectionsTab = ({
  starredProjects,
  recentProjects,
  highlightedIds,
  myBranches,
  featuredBranches,
  activeBranch,
  currentUserId,
  onFlyToStarred,
  onFlyToRecent,
  onUnstar,
  onActivateBranch,
  onEditBranch,
  onLoadBranchToSelection,
  onEnterMultiSelect,
  onFlyToProject,
  onOpenProject,
  onRemoveFromBranch,
  onRenameBranch,
  onUpdateBranchProjects,
  onSearchProjects,
  onDeleteBranch,
  allProjects = [],
  // Context-aware sort inputs (Option A) — null/empty means no re-ranking
  contextProject    = null,
  contextLineageIds = [],
}) => {
  const [collectionSearch, setCollectionSearch] = useState('');
  const searchInputRef = useRef(null);

  const allBranches = useMemo(() => {
    const myIds = new Set((myBranches || []).map(b => b.id));
    return {
      mine:     myBranches || [],
      featured: (featuredBranches || []).filter(b => !myIds.has(b.id)),
    };
  }, [myBranches, featuredBranches]);

  const q = collectionSearch.trim().toLowerCase();

  const {
    projectZoneMap,
    contextZoneId,
    lineageIdSet,
  } = useMemo(() => {
    const pzm = new Map();
    for (const p of (allProjects || [])) {
      const z = safeZone(p);
      if (z?.id) pzm.set(p.id, z.id);
    }
    const cz = contextProject ? (safeZone(contextProject)?.id ?? null) : null;
    const lset = new Set(contextLineageIds || []);
    if (contextProject?.id) lset.delete(contextProject.id);
    return { projectZoneMap: pzm, contextZoneId: cz, lineageIdSet: lset };
  }, [allProjects, contextProject, contextLineageIds]);

  const scoreBranch = useCallback((branch) => {
    if (!contextProject?.id) return 0;
    const ids = branch.curatedProjectIds || branch.curated_project_ids || [];
    if (ids.length === 0) return 0;

    let score = 0;

    if (ids.includes(contextProject.id)) score += 1000;

    if (contextZoneId) {
      let zoneHits = 0;
      for (const pid of ids) {
        if (projectZoneMap.get(pid) === contextZoneId) {
          zoneHits++;
          if (zoneHits >= 10) break;
        }
      }
      score += zoneHits * 10;
    }

    if (lineageIdSet.size > 0) {
      let lineageHits = 0;
      for (const pid of ids) {
        if (lineageIdSet.has(pid)) {
          lineageHits++;
          if (lineageHits >= 10) break;
        }
      }
      score += lineageHits * 50;
    }

    return score;
  }, [contextProject, contextZoneId, projectZoneMap, lineageIdSet]);


  const filteredStarred = useMemo(() => {
    if (!q) return starredProjects;
    return starredProjects.filter(p =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.author?.username || '').toLowerCase().includes(q) ||
      (p.tags || []).some(t => (typeof t === 'string' ? t : t.name || '').toLowerCase().includes(q))
    );
  }, [starredProjects, q]);

  const filteredRecents = useMemo(() => {
    if (!q) return recentProjects;
    return recentProjects.filter(p =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.author?.username || '').toLowerCase().includes(q)
    );
  }, [recentProjects, q]);

  const filteredBranches = useMemo(() => {
    const filterFn = (b) => !q || (b.name || '').toLowerCase().includes(q);

    const rank = (list) => {
      const decorated = list
        .map((b, i) => ({ branch: b, score: scoreBranch(b), originalIndex: i }))
        .filter(({ branch }) => filterFn(branch));

      decorated.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return a.originalIndex - b.originalIndex;
      });

      return decorated.map(d => d.branch);
    };

    return {
      mine:     rank(allBranches.mine),
      featured: rank(allBranches.featured),
    };
  }, [allBranches, q, scoreBranch]);

  const hasStarred  = filteredStarred.length > 0;
  const hasRecents  = filteredRecents.length > 0;
  const hasBranches = filteredBranches.mine.length > 0 || filteredBranches.featured.length > 0;
  const hasAnything = starredProjects.length > 0 || recentProjects.length > 0
                    || allBranches.mine.length > 0 || allBranches.featured.length > 0;
  const hasResults  = hasStarred || hasRecents || hasBranches;

  if (!hasAnything) {
    return (
      <div className="flex flex-col items-center justify-center py-6 text-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-zinc-800/80 border border-zinc-700/50 flex items-center justify-center">
          <FolderTree className="w-5 h-5 text-zinc-600" />
        </div>
        <div>
          <p className="text-[12px] font-semibold text-zinc-400">No collections yet</p>
          <p className="text-[11px] text-zinc-600 max-w-[200px] leading-relaxed mt-1">
            Star projects to bookmark them, or multi-select to save a curated collection.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 tray-item-stagger">

      {hasAnything && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 pointer-events-none" />
          <input
            ref={searchInputRef}
            type="text"
            value={collectionSearch}
            onChange={(e) => setCollectionSearch(e.target.value)}
            placeholder="Search collections..."
            className="w-full bg-zinc-800/50 border border-zinc-700/40 rounded-xl pl-9 pr-8 py-2 text-[12px] text-zinc-300 placeholder:text-zinc-600 focus:outline-none focus:border-purple-500/40 transition-colors"
          />
          {collectionSearch && (
            <button
              onClick={() => { setCollectionSearch(''); searchInputRef.current?.focus(); }}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {hasAnything && q && !hasResults && (
        <div className="flex flex-col items-center py-4 text-center gap-1.5">
          <Search className="w-4 h-4 text-zinc-600" />
          <p className="text-[11px] text-zinc-500">No matches for "{collectionSearch}"</p>
        </div>
      )}

      {hasRecents && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-3 h-3 text-zinc-500" />
            <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Recent</span>
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none" style={{ scrollbarWidth: 'none' }}>
            {filteredRecents.map(project => (
              <RecentPill
                key={project.id}
                project={project}
                onFlyTo={onFlyToRecent || onFlyToStarred}
                onAddToSelection={(p) => { onEnterMultiSelect?.(); onLoadBranchToSelection?.([p.id]); }}
              />
            ))}
          </div>
        </div>
      )}

      {hasRecents && (hasStarred || hasBranches) && (
        <div className="h-px bg-gradient-to-r from-transparent via-zinc-700/60 to-transparent" />
      )}

      {hasStarred && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
            <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Starred</span>
            <span className="ml-auto text-[10px] font-semibold text-zinc-600">{filteredStarred.length}</span>
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none" style={{ scrollbarWidth: 'none' }}>
            {filteredStarred.map(project => (
              <StarredPill
                key={project.id}
                project={project}
                isHighlighted={highlightedIds.includes(project.id)}
                onFlyTo={onFlyToStarred}
                onUnstar={onUnstar}
                onAddToSelection={(p) => { onEnterMultiSelect?.(); onLoadBranchToSelection?.([p.id]); }}
              />
            ))}
          </div>
        </div>
      )}

      {hasStarred && hasBranches && (
        <div className="h-px bg-gradient-to-r from-transparent via-zinc-700/60 to-transparent" />
      )}

      {hasBranches && (
        <div>
          <div className="flex items-center gap-2 mb-2.5">
            <FolderTree className="w-3 h-3 text-purple-400" />
            <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Collections</span>
            <span className="ml-auto text-[10px] font-semibold text-zinc-600">
              {filteredBranches.mine.length + filteredBranches.featured.length}
            </span>
          </div>

          <div className="space-y-2">
            {filteredBranches.mine.length > 0 && (
              <>
                {filteredBranches.featured.length > 0 && (
                  <p className="text-[9px] text-zinc-700 font-bold uppercase tracking-widest px-0.5">Yours</p>
                )}
                {filteredBranches.mine.map(branch => (
                  <BranchViewer
                    key={branch.id}
                    branch={branch}
                    isActive={activeBranch?.id === branch.id}
                    isOwned={true}
                    onActivate={onActivateBranch}
                    onEdit={onEditBranch}
                    onAddToSelection={(b) => onLoadBranchToSelection?.(b.curatedProjectIds || [])}
                    onFlyToProject={onFlyToProject}
                    onOpenProject={onOpenProject}
                    onRemoveFromBranch={onRemoveFromBranch}
                    onRenameBranch={onRenameBranch}
                    onUpdateBranchProjects={onUpdateBranchProjects}
                    onSearchProjects={onSearchProjects}
                    onDeleteBranch={onDeleteBranch}
                    allProjects={allProjects}
                  />
                ))}
              </>
            )}
            {filteredBranches.featured.length > 0 && (
              <>
                {filteredBranches.mine.length > 0 && (
                  <p className="text-[9px] text-zinc-700 font-bold uppercase tracking-widest px-0.5 mt-3">Featured</p>
                )}
                {filteredBranches.featured.map(branch => (
                  <BranchViewer
                    key={branch.id}
                    branch={branch}
                    isActive={activeBranch?.id === branch.id}
                    isOwned={branch.creatorId === currentUserId}
                    onActivate={onActivateBranch}
                    onEdit={onEditBranch}
                    onAddToSelection={(b) => onLoadBranchToSelection?.(b.curatedProjectIds || [])}
                    onFlyToProject={onFlyToProject}
                    onOpenProject={onOpenProject}
                    onRemoveFromBranch={onRemoveFromBranch}
                    onRenameBranch={onRenameBranch}
                    onUpdateBranchProjects={onUpdateBranchProjects}
                    onSearchProjects={onSearchProjects}
                    onDeleteBranch={onDeleteBranch}
                    allProjects={allProjects}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {activeBranch && activeBranch.creatorId === currentUserId && (
        <div className="flex items-center gap-2.5 p-3 bg-amber-500/6 border border-amber-500/20 rounded-xl">
          <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
          <p className="text-[10px] text-amber-400/80 leading-relaxed flex-1">
            <strong className="font-semibold">"{activeBranch.name}"</strong> is active on the map.{' '}
            <button
              className="underline underline-offset-2 hover:text-amber-300 transition-colors"
              onClick={() => onLoadBranchToSelection?.(activeBranch.curatedProjectIds || [])}
            >
              Load to edit.
            </button>
          </p>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SELECT TAB CONTENT
// ─────────────────────────────────────────────────────────────────────────────

const SelectTab = ({
  selectedProjects,
  multiSelectedIds,
  isMultiSelectMode,
  isEditingBranch,
  activeBranch,
  totalProjectCount,
  isCreateDocked,
  dockedCreateParents,
  onRemoveProject,
  onAddProject,
  onClearAll,
  onForkSelected,
  onCurateSelected,
  onExitMultiSelect,
  onSaveBranchEdits,
  onSearchProjects,
}) => {
  const [showSearch, setShowSearch]     = useState(false);
  const [justAddedIds, setJustAddedIds] = useState(new Set());
  const scrollRef     = useRef(null);
  const selectedCount = multiSelectedIds.length;

  const displayProjects = isCreateDocked
    ? [...selectedProjects, ...(dockedCreateParents || []).filter(p => !multiSelectedIds.includes(p.id))]
    : selectedProjects;

  const handleAddProject = useCallback((project) => {
    onAddProject?.(project);
    setJustAddedIds(prev => new Set([...prev, project.id]));
    setTimeout(() => {
      setJustAddedIds(prev => { const next = new Set(prev); next.delete(project.id); return next; });
    }, 2000);
    setTimeout(() => {
      scrollRef.current?.scrollTo({ left: scrollRef.current.scrollWidth, behavior: 'smooth' });
    }, 100);
  }, [onAddProject]);

  return (
    <>
      <SearchToAdd
        isVisible={showSearch}
        onSearch={onSearchProjects}
        onAdd={handleAddProject}
        selectedIds={multiSelectedIds}
      />

      <div className="flex items-center gap-2 mb-2.5">
        <div
          ref={scrollRef}
          className="flex items-center gap-1.5 overflow-x-auto flex-1 min-w-0 pb-0.5 scrollbar-none"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {displayProjects.length > 0
            ? displayProjects.map(project => (
              <SelectedProjectPill
                key={project.id}
                project={project}
                onRemove={onRemoveProject}
                isJustAdded={justAddedIds.has(project.id)}
              />
            ))
            : (
              <div className="flex items-center gap-2 px-3 py-2 text-zinc-600">
                <Sparkles className="w-3.5 h-3.5" />
                <span className="text-[12px]">
                  {isMultiSelectMode ? 'Click any node to select it' : 'No projects selected'}
                </span>
              </div>
            )
          }
        </div>
        <button
          onClick={() => setShowSearch(!showSearch)}
          className={cn(
            'p-2 rounded-xl border transition-all flex-shrink-0',
            showSearch
              ? 'bg-purple-500/15 border-purple-500/40 text-purple-400'
              : 'bg-zinc-800/60 border-zinc-700/40 text-zinc-500 hover:text-zinc-300 hover:border-zinc-600',
          )}
          title="Search to add"
        >
          {showSearch ? <X className="w-3.5 h-3.5" /> : <Search className="w-3.5 h-3.5" />}
        </button>
      </div>

      {displayProjects.length >= 2 && (
        <div className="mb-2.5">
          <ZoneDiversityBadge selectedProjects={displayProjects} />
        </div>
      )}

      <div className="flex items-center gap-2">
        {isEditingBranch ? (
          <>
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-500/10 border border-amber-500/25 rounded-lg flex-shrink-0">
              <FolderTree className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-[12px] font-medium text-amber-400 max-w-[120px] truncate">
                {activeBranch.name || 'Branch'}
              </span>
            </div>
            <span className="text-[11px] text-zinc-500 flex-shrink-0">
              {selectedCount} project{selectedCount !== 1 ? 's' : ''}
            </span>
            <div className="flex-1" />
            <Button variant="primary" size="sm" icon={Check} onClick={onSaveBranchEdits} className="flex-shrink-0">Save</Button>
            <button onClick={onExitMultiSelect} className="p-1.5 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 rounded-lg transition-colors flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </>
        ) : (
          <>
            <span className="text-[11px] text-zinc-500 flex-shrink-0">
              {selectedCount} of {totalProjectCount}
            </span>
            <div className="flex-1" />
            {selectedCount >= 2 && (
              <>
                <Button variant="primary" size="sm" icon={GitFork} onClick={onForkSelected} className="flex-shrink-0">
                  Fork ({selectedCount})
                </Button>
                <Button variant="secondary" size="sm" icon={FolderTree} onClick={onCurateSelected} className="flex-shrink-0">
                  Save
                </Button>
              </>
            )}
            {selectedCount >= 1 && (
              <button onClick={onClearAll} className="px-2.5 py-1.5 text-[12px] text-zinc-500 hover:text-zinc-300 transition-colors flex-shrink-0">
                Clear
              </button>
            )}
            <button onClick={onExitMultiSelect} className="p-1.5 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 rounded-lg transition-colors flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </>
        )}
      </div>

      {isMultiSelectMode && selectedCount === 0 && (
        <p className="text-[10px] text-zinc-600 mt-2 text-center">
          Click any node on the map to select it — or search above
        </p>
      )}
      {isCreateDocked && (
        <p className="text-[10px] text-purple-400/60 mt-2 text-center">
          Adding parents for your new project
        </p>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// JOURNEY TAB — Two-state lineage tree (mirrors Journey page's Creative Lineage)
// ─────────────────────────────────────────────────────────────────────────────

const ChallengeCard = ({ challenge }) => {
  const title = challenge?.challenge_title || challenge?.challengeTitle
    || challenge?.title || challenge?.name || 'Challenge';
  const responseCount = challenge?.responseCount || challenge?.response_count || 0;
  const isOpen = responseCount === 0;

  const S = 28;
  const points = `${S},0 ${S*2},${S} ${S},${S*2} 0,${S}`;

  return (
    <div className="flex flex-col items-center gap-1.5 select-none pointer-events-none">
      <div className="relative flex items-center justify-center" style={{ width: S * 2, height: S * 2 }}>
        <svg
          width={S * 2} height={S * 2}
          viewBox={`0 0 ${S * 2} ${S * 2}`}
          style={{ overflow: 'visible' }}
        >
          {isOpen && (
            <polygon points={points} fill="none" stroke="#f59e0b" strokeWidth="1" opacity="0.3"
              transform={`scale(1.35) translate(-${S * 0.175},-${S * 0.175})`}>
              <animate attributeName="opacity" values="0.1;0.35;0.1" dur="2.5s" repeatCount="indefinite" />
              <animateTransform attributeName="transform" type="scale"
                values={`1.25 translate(-${S*0.125},-${S*0.125});1.45 translate(-${S*0.225},-${S*0.225});1.25 translate(-${S*0.125},-${S*0.125})`}
                dur="2.5s" repeatCount="indefinite" additive="sum" />
            </polygon>
          )}
          <polygon
            points={points}
            fill="#1c1917"
            stroke="#f59e0b"
            strokeWidth="2"
          />
          <text
            x={S} y={S}
            textAnchor="middle"
            dominantBaseline="central"
            fill={isOpen ? '#fbbf24' : '#f59e0b'}
            fontSize={isOpen ? '14' : '12'}
            fontWeight="700"
          >
            {isOpen ? '?' : responseCount}
          </text>
        </svg>
      </div>

      <div className="max-w-[120px] text-center">
        <p className="text-[10px] font-semibold text-amber-400 leading-tight truncate">{title}</p>
        <p className="text-[9px] text-amber-700 leading-tight">
          {isOpen ? 'open challenge' : `${responseCount} response${responseCount !== 1 ? 's' : ''}`}
        </p>
      </div>
    </div>
  );
};

const TreeCard = ({ project, isCurrent, onClick }) => {
  if (project?._isChallenge) {
    return <ChallengeCard challenge={project._challengeData || project} />;
  }

  const zone = safeZone(project) || {};
  const { color } = zoneInfo(zone.id || 'default');
  const img = project?.image_url || project?.imageUrl || project?.cover_image || project?.coverImage;

  if (isCurrent) {
    return (
      <button
        onClick={() => onClick?.(project)}
        className="flex items-center gap-3 p-3 rounded-xl border-2 border-purple-500/50 bg-purple-500/10 text-left transition-all active:scale-[0.98] w-full max-w-xs mx-auto"
      >
        <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 border border-purple-500/30">
          {img ? (
            <img src={img} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
              <Sparkles className="w-5 h-5 text-white" />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-white truncate">{project?.name}</p>
          <p className="text-[11px] text-purple-400">by {project?.author?.username || 'Unknown'}</p>
        </div>
      </button>
    );
  }

  return (
    <button
      onClick={() => onClick?.(project)}
      className="flex items-center gap-3 p-2.5 rounded-xl bg-zinc-900 border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800/50 text-left transition-all active:scale-[0.98] group w-full max-w-[200px]"
    >
      <div
        className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border"
        style={{ borderColor: `${color}30` }}
      >
        {img ? (
          <img src={img} alt="" className="w-full h-full object-cover" />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center font-bold text-xs"
            style={{ backgroundColor: `${color}15`, color }}
          >
            {project?.name?.[0]?.toUpperCase() || '?'}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-medium text-zinc-300 truncate group-hover:text-purple-300 transition-colors">
          {project?.name}
        </p>
        <p className="text-[10px] text-zinc-500 truncate">by {project?.author?.username || 'Unknown'}</p>
        <p className="text-[10px] mt-0.5" style={{ color }}>{zone.shortLabel || ''}</p>
      </div>
    </button>
  );
};

const TreeForkDivider = ({ direction = 'down', crossZone, fromZone, toZone, horizontal = false }) => {
  if (horizontal) {
    return (
      <div className="flex flex-col items-center justify-center px-1 flex-shrink-0" style={{ minWidth: 32 }}>
        {crossZone && fromZone && toZone && (
          <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-fuchsia-500/8 border border-fuchsia-500/15 mb-1">
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: zoneInfo(fromZone).color }} />
            <Zap className="w-2 h-2 text-fuchsia-400" />
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: zoneInfo(toZone).color }} />
          </div>
        )}
        <div className="flex items-center text-zinc-600">
          <div className="h-px w-3 bg-zinc-700" />
          <GitFork className="w-3 h-3 -rotate-90" />
          <div className="h-px w-3 bg-zinc-700" />
        </div>
      </div>
    );
  }
  return (
  <div className="flex flex-col items-center gap-0 py-1">
    {crossZone && fromZone && toZone && (
      <div className="flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-fuchsia-500/8 border border-fuchsia-500/15 mb-1">
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: zoneInfo(fromZone).color }} />
        <Zap className="w-2.5 h-2.5 text-fuchsia-400" />
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: zoneInfo(toZone).color }} />
      </div>
    )}
    <div className="flex items-center gap-2 text-zinc-600">
      <div className="w-px h-5 bg-zinc-700" />
      <GitFork className={cn('w-4 h-4', direction === 'up' && 'rotate-180')} />
      <div className="w-px h-5 bg-zinc-700" />
    </div>
  </div>
  );
};

const JourneyTab = ({ lineage, currentProject, onSelectProject, onViewProject, onGrabToSelection, onClose }) => {
  const projects     = lineage || [];
  const currentId    = currentProject?.id;
  const scrollRef    = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [treeExpanded, setTreeExpanded] = useState(false);

  useEffect(() => {
    if (currentId) {
      const idx = projects.findIndex(p => p.id === currentId);
      if (idx !== -1) setCurrentIndex(idx);
    }
  }, [currentId]);

  const handleCardClick = useCallback((project) => {
    onViewProject?.(project);
  }, [onViewProject]);

  const crossZoneCount = useMemo(() => {
    let n = 0;
    for (let i = 1; i < projects.length; i++) {
      if ((projects[i].zone || 'default') !== (projects[i - 1].zone || 'default')) n++;
    }
    return n;
  }, [projects]);

  if (projects.length === 0) return (
    <div className="flex flex-col items-center justify-center py-4 text-center gap-2">
      <GitBranch className="w-5 h-5 text-zinc-700" />
      <p className="text-[11px] text-zinc-600 max-w-[220px] leading-relaxed">Click a connection line to explore a creative lineage here.</p>
    </div>
  );

  const currentIdx = projects.findIndex(p => !p._isChallenge && p.id === currentId);
  const safeCurrIdx = currentIdx >= 0 ? currentIdx : currentIndex;
  const ancestors   = projects.slice(0, safeCurrIdx);
  const current     = projects[safeCurrIdx];
  const descendants = projects.slice(safeCurrIdx + 1);

  const hasAncestors   = ancestors.length > 0;
  const hasDescendants = descendants.length > 0;

  const lastRealAncestor    = hasAncestors ? [...ancestors].reverse().find(p => !p._isChallenge) : null;
  const firstRealDescendant = hasDescendants ? descendants.find(p => !p._isChallenge) : null;

  const lastAncestorZone = lastRealAncestor?.zone || 'default';
  const currentZone      = current?.zone || 'default';
  const firstDescZone    = firstRealDescendant?.zone || 'default';

  const ancestorCrossZone   = lastRealAncestor && lastAncestorZone !== currentZone;
  const descendantCrossZone = firstRealDescendant && currentZone !== firstDescZone;

  const realAncestorCount   = ancestors.filter(p => !p._isChallenge).length;
  const realDescendantCount = descendants.filter(p => !p._isChallenge).length;
  const challengeCount      = projects.filter(p => p._isChallenge).length;

  const touchStartY = useRef(null);
  const handleTouchStart = useCallback((e) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);
  const handleTouchEnd = useCallback((e) => {
    if (touchStartY.current === null) return;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;
    touchStartY.current = null;
    if (deltaY < -40 && (hasAncestors || hasDescendants)) {
      setTreeExpanded(true);
    } else if (deltaY > 40 && treeExpanded) {
      setTreeExpanded(false);
    }
  }, [hasAncestors, hasDescendants, treeExpanded]);

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="flex justify-center mb-2 md:hidden">
        <div className="w-8 h-1 rounded-full bg-zinc-700" />
      </div>

      {!treeExpanded && (
        <div className="flex flex-col items-center gap-2">
          {current && (
            <TreeCard project={current} isCurrent={true} onClick={handleCardClick} />
          )}

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-[11px] text-zinc-500">
              {realAncestorCount > 0 && (
                <span className="flex items-center gap-1">
                  <GitFork className="w-3 h-3 rotate-180" />
                  {realAncestorCount} ancestor{realAncestorCount !== 1 ? 's' : ''}
                </span>
              )}
              {realDescendantCount > 0 && (
                <span className="flex items-center gap-1">
                  <GitFork className="w-3 h-3" />
                  {realDescendantCount} fork{realDescendantCount !== 1 ? 's' : ''}
                </span>
              )}
              {challengeCount > 0 && (
                <span className="flex items-center gap-1 text-amber-500/70">
                  <svg width="10" height="10" viewBox="0 0 10 10"><polygon points="5,0 10,5 5,10 0,5" fill="none" stroke="#f59e0b" strokeWidth="1.5"/></svg>
                  {challengeCount} challenge{challengeCount !== 1 ? 's' : ''}
                </span>
              )}
              {crossZoneCount > 0 && (
                <span className="flex items-center gap-1 text-fuchsia-400/60">
                  <Zap className="w-3 h-3" />
                  {crossZoneCount} leap{crossZoneCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            {(hasAncestors || hasDescendants) && (
              <button
                onClick={() => setTreeExpanded(true)}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-purple-500/10 text-purple-400 text-[11px] font-medium hover:bg-purple-500/20 transition-colors"
              >
                <ChevronUp className="w-3 h-3" />
                Show tree
              </button>
            )}
          </div>
        </div>
      )}

      {treeExpanded && (
        <div
          ref={scrollRef}
          className="overflow-y-auto"
          style={{ maxHeight: '30vh', scrollbarWidth: 'thin', scrollbarColor: '#3f3f46 transparent' }}
        >
          <div className="flex flex-col items-center gap-1 pb-2">

            {hasAncestors && (
              <>
                <div className="flex flex-wrap justify-center gap-2">
                  {ancestors.map(p => (
                    <TreeCard key={p.id} project={p} onClick={handleCardClick} />
                  ))}
                </div>
                <TreeForkDivider
                  direction="up"
                  crossZone={ancestorCrossZone}
                  fromZone={lastAncestorZone}
                  toZone={currentZone}
                />
              </>
            )}

            {current && (
              <TreeCard project={current} isCurrent={true} onClick={handleCardClick} />
            )}

            {hasDescendants && (
              <>
                <TreeForkDivider
                  direction="down"
                  crossZone={descendantCrossZone}
                  fromZone={currentZone}
                  toZone={firstDescZone}
                />
                <div className="flex flex-wrap justify-center gap-2">
                  {descendants.map(p => (
                    <TreeCard key={p.id} project={p} onClick={handleCardClick} />
                  ))}
                </div>
              </>
            )}

            {!hasAncestors && !hasDescendants && (
              <p className="text-[10px] text-zinc-600 text-center py-1">
                Original project — no forks yet
              </p>
            )}
          </div>

          <div className="flex justify-center pt-1 pb-0.5">
            <button
              onClick={() => setTreeExpanded(false)}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50 transition-colors"
            >
              <ChevronDown className="w-3 h-3" />
              Collapse
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN SELECTION TRAY
// ─────────────────────────────────────────────────────────────────────────────

const SelectionTray = ({
  // Visibility
  isVisible,
  alwaysShow = true,
  onCollapse,
  onExpandChange,

  // Selection state
  selectedProjects   = [],
  multiSelectedIds   = [],
  isMultiSelectMode,

  // Selection actions
  onRemoveProject,
  onAddProject,
  onClearAll,
  onForkSelected,
  onCurateSelected,
  onExitMultiSelect,
  onEnterMultiSelect,

  // Branch editing
  activeBranch = null,
  onSaveBranchEdits,

  // Search
  onSearchProjects,

  // CreateProjectOverlay dock
  isCreateDocked    = false,
  dockedCreateParents = [],

  // Collections data
  starredProjects  = [],
  recentProjects   = [],
  highlightedIds   = [],
  myBranches       = [],
  featuredBranches = [],
  currentUserId,

  // Collections actions
  onFlyToStarred,
  onFlyToRecent,
  onUnstar,
  onActivateBranch,
  onEditBranch,
  onLoadBranchToSelection,

  // All map projects — used to hydrate branch IDs → project objects
  allProjects = [],

  // Branch item actions
  onFlyToProject,
  onOpenProject,
  onRemoveFromBranch,
  onRenameBranch,
  onUpdateBranchProjects,
  onDeleteBranch,

  // Journey (scrubber)
  journeyLineage   = [],
  journeyProject   = null,
  onJourneySelect,
  onJourneyView,
  onJourneyGrab,
  onJourneyClose,

  // Counts
  totalProjectCount = 0,

  // Context-aware collections sort (Option A)
  contextProject    = null,
  contextLineageIds = [],
}) => {
  const [activeTab, setActiveTab]   = useState('collections');
  const [isExpanded, setIsExpanded] = useState(false);

  // Notify parent whenever expanded state changes
  useEffect(() => {
    onExpandChange?.(isExpanded);
  }, [isExpanded, onExpandChange]);

  const JOURNEY_ENABLED = true;
  const hasJourney      = JOURNEY_ENABLED && journeyLineage.length >= 1;

  const hasCollections  = starredProjects.length > 0 || recentProjects.length > 0
                        || myBranches.length > 0 || featuredBranches.length > 0;
  const isEditingBranch = isMultiSelectMode && !!activeBranch;
  const selectedCount   = multiSelectedIds.length;

  // ── Auto-routing ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (isMultiSelectMode) { setActiveTab('select'); setIsExpanded(true); }
  }, [isMultiSelectMode]);

  useEffect(() => {
    if (!isMultiSelectMode && activeTab === 'select') setActiveTab('collections');
  }, [isMultiSelectMode]);

  useEffect(() => {
    if (hasJourney) { setActiveTab('journey'); setIsExpanded(true); }
  }, [hasJourney, journeyLineage.length]);

  // ── FIXED: handleGrabToSelection defined BEFORE the early return ──────────
  const handleGrabToSelection = useCallback((project) => {
    if (!project) return;
    onEnterMultiSelect?.();
    onJourneyGrab?.(project);
    setActiveTab('select');
  }, [onEnterMultiSelect, onJourneyGrab]);

  // ── Visibility — tray is ALWAYS present
  const shouldShow = true;
  if (!shouldShow) return null;

  const selectLabel = isMultiSelectMode
    ? `${selectedCount > 0 ? `${selectedCount} ` : ''}Selected`
    : 'Select';

  const glowColor = isEditingBranch
    ? 'rgba(245,158,11,0.06)'
    : activeTab === 'journey'
    ? 'rgba(168,85,247,0.07)'
    : activeTab === 'select'
    ? 'rgba(99,102,241,0.06)'
    : 'rgba(139,92,246,0.05)';

  const topBorderColor = isEditingBranch
    ? '#f59e0b'
    : activeTab === 'journey'
    ? '#a855f7'
    : activeTab === 'select' && isEditingBranch
    ? '#f59e0b'
    : '#6366f1';

  const handleTabClick = (tab) => {
    if (activeTab === tab && isExpanded) {
      setIsExpanded(false);
    } else {
      setActiveTab(tab);
      setIsExpanded(true);
    }
  };

  const tabs = [
    {
      id: 'collections',
      label: 'Collections',
      icon: Bookmark,
      badge: hasCollections ? (starredProjects.length + myBranches.length + featuredBranches.length) : null,
      show: true,
      activeColor: '#a855f7',
    },
    {
      id: 'select',
      label: isEditingBranch ? activeBranch?.name?.slice(0, 12) || 'Branch' : selectLabel,
      icon: isEditingBranch ? FolderTree : Layers,
      badge: selectedCount > 0 && !isEditingBranch ? selectedCount : null,
      show: isMultiSelectMode || activeTab === 'select',
      activeColor: isEditingBranch ? '#f59e0b' : '#6366f1',
    },
    {
      id: 'journey',
      label: 'Journey',
      icon: GitBranch,
      badge: hasJourney ? journeyLineage.length : null,
      show: true,
      activeColor: '#a855f7',
    },
  ].filter(t => t.show);

  return (
    <>
      {isExpanded && !isMultiSelectMode && (
        <div className="fixed inset-0" style={{ zIndex: 39 }} onClick={() => setIsExpanded(false)} />
      )}

      <div
        className={cn(
          'fixed bottom-0 left-0 right-0 z-40 pointer-events-none',
          'transition-all duration-400 ease-out',
          isExpanded ? 'translate-y-0' : 'translate-y-[calc(100%-44px)]',
        )}
        style={{ transition: 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)' }}
      >
        <div className="pointer-events-auto">

          <div className="flex items-end justify-center gap-1 px-4 mb-0">

            <div className="flex items-end gap-1">
              {tabs.map(tab => {
                const isActive = activeTab === tab.id && isExpanded;
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabClick(tab.id)}
                    className={cn(
                      'relative flex items-center gap-1.5 px-3.5 py-2 rounded-t-2xl border border-b-0',
                      'transition-all duration-200 text-[11px] font-semibold tracking-wide',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/50',
                    )}
                    style={{
                      background: isActive
                        ? `linear-gradient(180deg, ${tab.activeColor}18 0%, rgba(9,9,11,0.98) 100%)`
                        : 'rgba(12,12,14,0.85)',
                      borderColor: isActive ? `${tab.activeColor}35` : 'rgba(63,63,70,0.5)',
                      color: isActive ? '#e4e4e7' : '#71717a',
                      backdropFilter: 'blur(20px)',
                      paddingBottom: isActive ? '10px' : '8px',
                    }}
                  >
                    {isActive && (
                      <div
                        className="absolute top-0 left-3 right-3 h-[1.5px] rounded-full"
                        style={{ background: `linear-gradient(90deg, transparent, ${tab.activeColor}80, transparent)` }}
                      />
                    )}
                    <Icon className="w-3 h-3 flex-shrink-0" style={{ color: isActive ? tab.activeColor : undefined }} />
                    <span>{tab.label}</span>
                    {tab.badge != null && (
                      <span
                        className="flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold"
                        style={{
                          background: isActive ? tab.activeColor : 'rgba(63,63,70,0.8)',
                          color: isActive ? '#fff' : '#a1a1aa',
                        }}
                      >
                        {tab.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {isExpanded && (
              <button
                onClick={() => setIsExpanded(false)}
                className="flex items-center gap-1.5 px-3 py-1.5 mb-0.5 rounded-xl text-[11px] font-medium text-zinc-500 hover:text-zinc-300 transition-colors"
                style={{ background: 'rgba(24,24,27,0.7)', backdropFilter: 'blur(12px)' }}
              >
                <ChevronDown className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Close</span>
              </button>
            )}
          </div>

          <div
            className="border-t overflow-y-auto"
            style={{
              background: `linear-gradient(180deg, ${glowColor} 0%, rgba(7,7,9,0.97) 40%)`,
              borderColor: `${topBorderColor}25`,
              backdropFilter: 'blur(40px) saturate(180%)',
              WebkitBackdropFilter: 'blur(40px) saturate(180%)',
              boxShadow: `0 -1px 0 ${topBorderColor}20, 0 -12px 40px rgba(0,0,0,0.5), 0 -2px 8px rgba(0,0,0,0.3)`,
              maxHeight: activeTab === 'select' ? '25vh' : activeTab === 'journey' ? '35vh' : '42vh',
              scrollbarWidth: 'thin',
              scrollbarColor: '#3f3f46 transparent',
            }}
          >
            <div className="h-px w-full" style={{
              background: `linear-gradient(90deg, transparent 0%, ${topBorderColor}40 30%, ${topBorderColor}60 50%, ${topBorderColor}40 70%, transparent 100%)`,
            }} />

            <div className="px-4 py-3">
              {activeTab === 'collections' && (
                <CollectionsTab
                  starredProjects={starredProjects}
                  recentProjects={recentProjects}
                  highlightedIds={highlightedIds}
                  myBranches={myBranches}
                  featuredBranches={featuredBranches}
                  activeBranch={activeBranch}
                  currentUserId={currentUserId}
                  onFlyToStarred={onFlyToStarred}
                  onFlyToRecent={onFlyToRecent || onFlyToStarred}
                  onUnstar={onUnstar}
                  onActivateBranch={onActivateBranch}
                  onEditBranch={onEditBranch}
                  onLoadBranchToSelection={onLoadBranchToSelection}
                  onEnterMultiSelect={onEnterMultiSelect}
                  onFlyToProject={onFlyToProject}
                  onOpenProject={onOpenProject}
                  onRemoveFromBranch={onRemoveFromBranch}
                  onRenameBranch={onRenameBranch}
                  onUpdateBranchProjects={onUpdateBranchProjects}
                  onSearchProjects={onSearchProjects}
                  onDeleteBranch={onDeleteBranch}
                  allProjects={allProjects}
                  contextProject={contextProject}
                  contextLineageIds={contextLineageIds}
                />
              )}

              {activeTab === 'select' && (
                <SelectTab
                  selectedProjects={selectedProjects}
                  multiSelectedIds={multiSelectedIds}
                  isMultiSelectMode={isMultiSelectMode}
                  isEditingBranch={isEditingBranch}
                  activeBranch={activeBranch}
                  totalProjectCount={totalProjectCount}
                  isCreateDocked={isCreateDocked}
                  dockedCreateParents={dockedCreateParents}
                  onRemoveProject={onRemoveProject}
                  onAddProject={onAddProject}
                  onClearAll={onClearAll}
                  onForkSelected={onForkSelected}
                  onCurateSelected={onCurateSelected}
                  onExitMultiSelect={onExitMultiSelect}
                  onSaveBranchEdits={onSaveBranchEdits}
                  onSearchProjects={onSearchProjects}
                />
              )}

              {activeTab === 'journey' && hasJourney && (
                <JourneyTab
                  lineage={journeyLineage}
                  currentProject={journeyProject}
                  onSelectProject={onJourneySelect}
                  onViewProject={onJourneyView}
                  onGrabToSelection={handleGrabToSelection}
                  onClose={onJourneyClose}
                />
              )}

              {activeTab === 'journey' && !hasJourney && (
                <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                  <GitBranch className="w-6 h-6 text-purple-500/30 mb-2.5" />
                  <p className="text-[13px] font-medium text-zinc-400 mb-1">No active journey</p>
                  <p className="text-[11px] text-zinc-600 leading-relaxed max-w-[240px]">
                    Click a project on the map to trace its creative lineage. The journey will appear here so you can browse it from any page.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes trayDropdownIn {
          from { opacity: 0; transform: translateY(6px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes staggerFadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .tray-item-stagger > * {
          animation: staggerFadeUp 0.25s ease-out both;
        }
        .tray-item-stagger > *:nth-child(1) { animation-delay: 0ms; }
        .tray-item-stagger > *:nth-child(2) { animation-delay: 40ms; }
        .tray-item-stagger > *:nth-child(3) { animation-delay: 80ms; }
        .tray-item-stagger > *:nth-child(4) { animation-delay: 120ms; }
        .tray-item-stagger > *:nth-child(5) { animation-delay: 160ms; }
        .scrollbar-none::-webkit-scrollbar { display: none; }
      `}</style>
    </>
  );
};

export default SelectionTray;
